import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { usePermissions } from '../context/PermissionsContext';
import '../ui-pages.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// ---- inline icon helpers (stroke SVG, no emoji) ----
const Ic = ({ d, size = 18, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...rest}>
    {d}
  </svg>
);

const IconBolt = (p) => <Ic {...p} d={<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />} />;
const IconPlus = (p) => <Ic {...p} d={<><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>} />;
const IconPower = (p) => <Ic {...p} d={<><path d="M18.36 6.64a9 9 0 1 1-12.73 0" /><line x1="12" y1="2" x2="12" y2="12" /></>} />;
const IconSearch = (p) => <Ic {...p} d={<><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>} />;
const IconEdit = (p) => <Ic {...p} d={<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></>} />;
const IconTrash = (p) => <Ic {...p} d={<><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>} />;
const IconTarget = (p) => <Ic {...p} d={<><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></>} />;
const IconClose = (p) => <Ic {...p} d={<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>} />;

// platform glyphs
const IconWhatsApp = (p) => <Ic {...p} d={<><path d="M21 11.5a8.5 8.5 0 0 1-12.6 7.4L3 21l2.2-5.3A8.5 8.5 0 1 1 21 11.5z" /><path d="M8.5 8.5c0 4 3 7 7 7" /></>} />;
const IconInstagram = (p) => <Ic {...p} d={<><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="3.5" /><line x1="17.5" y1="6.5" x2="17.5" y2="6.5" /></>} />;
const IconWebsite = (p) => <Ic {...p} d={<><circle cx="12" cy="12" r="9" /><line x1="3" y1="12" x2="21" y2="12" /><path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18z" /></>} />;
const IconMessenger = (p) => <Ic {...p} d={<><path d="M12 2C6.5 2 2 6.1 2 11.2c0 2.9 1.4 5.4 3.7 7.1V22l3.4-1.9c.9.3 1.9.4 2.9.4 5.5 0 10-4.1 10-9.2S17.5 2 12 2z" /><path d="m7 13 3-3 2 2 3-3" /></>} />;

// Brand channel colours — identical to the Chats page (PLATFORM_BRAND), so the
// channel glyphs read the same across Chats, Integrations and Automations.
const PLATFORMS = [
  { v: 'whatsapp', label: 'WhatsApp', Icon: IconWhatsApp, tone: 'emerald', color: '#16a34a', bg: '#dcfce7' },
  { v: 'instagram', label: 'Instagram', Icon: IconInstagram, tone: 'pink', color: '#db2777', bg: '#fce7f3' },
  { v: 'facebook', label: 'Messenger', Icon: IconMessenger, tone: 'blue', color: '#1d4ed8', bg: '#e0e7ff' },
  { v: 'website', label: 'Website', Icon: IconWebsite, tone: 'indigo', color: '#4338ca', bg: '#e0e7ff' },
];

const platformMeta = (p) => PLATFORMS.find(x => x.v === p) || { v: p, label: p, Icon: IconWebsite, tone: 'slate', color: '#6B6862', bg: '#FAF7F1' };

const AutomationsList = () => {
  const { workspaceUuid } = useParams();
  const navigate = useNavigate();
  const { readOnly } = usePermissions();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create-modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState('');
  const [availableIntents, setAvailableIntents] = useState([]);
  const [form, setForm] = useState({ platforms: ['whatsapp'], triggerType: 'intent', intent: '', event: 'new_message', inactivityIntervals: ['3h'], expiryH: '2', expiryM: '0', name: '', editId: null });

  const EVENT_OPTIONS = [
    { v: 'new_message', label: 'New message received', platforms: ['whatsapp', 'instagram', 'facebook', 'website'] },
    { v: 'new_order', label: 'New order (Shopify)', platforms: ['whatsapp'] },
    { v: 'new_abandoned_checkout', label: 'New abandoned checkout (Shopify)', platforms: ['whatsapp'] },
    { v: 'agent_query_resolved', label: 'Agent: Query Resolved (internal)', platforms: ['whatsapp', 'instagram', 'facebook', 'website'] },
  ];
  // Events available to EVERY selected platform (intersection).
  const commonEventsFor = (platforms) => EVENT_OPTIONS.filter(o => platforms.length > 0 && platforms.every(p => o.platforms.includes(p)));

  // UI-only state (does not affect data flow)
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchWorkflows();
  }, [workspaceUuid]);

  const fetchWorkflows = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/workflows?workspaceUuid=${workspaceUuid}`);
      setWorkflows(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchIntents = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tags?workspaceUuid=${workspaceUuid}`);
      setAvailableIntents(res.data || []);
    } catch (err) {
      console.error('Failed to load intents', err);
    }
  };

  const handleCreateNew = () => {
    // Demo accounts CAN walk the full create flow (popup + editor); only the
    // final save is blocked (see handleSubmitCreate / editor Save & Deploy).
    setForm({ platforms: ['whatsapp'], triggerType: 'intent', intent: '', event: 'new_message', inactivityIntervals: ['3h'], expiryH: '2', expiryM: '0', name: '', editId: null });
    setCreateErr('');
    fetchIntents();
    setShowCreateModal(true);
  };

  // Open the modal in EDIT mode to change an automation's name + platforms.
  const handleEditConfig = (wf) => {
    const platforms = (wf.platforms && wf.platforms.length) ? wf.platforms : [wf.platform || 'whatsapp'];
    setForm({
      platforms,
      triggerType: wf.trigger?.type || 'intent',
      intent: wf.trigger?.type === 'intent' ? (wf.trigger?.value || '') : '',
      event: wf.trigger?.type === 'event' ? (wf.trigger?.value || 'new_message') : 'new_message',
      inactivityIntervals: wf.trigger?.type === 'inactivity' ? (wf.trigger?.value ? String(wf.trigger.value).split(',') : ['3h']) : ['3h'],
      expiryH: wf.trigger?.type === 'chat_expiry_reminder' ? String(Math.floor(Number(wf.trigger.value || 120) / 60)) : '2',
      expiryM: wf.trigger?.type === 'chat_expiry_reminder' ? String(Number(wf.trigger.value || 120) % 60) : '0',
      name: wf.name || '',
      editId: wf._id,
    });
    setCreateErr('');
    fetchIntents();
    setShowCreateModal(true);
  };

  // Toggle a platform in/out of the selection; keep the chosen event valid.
  const togglePlatform = (p) => {
    setForm(prev => {
      let platforms = prev.platforms.includes(p) ? prev.platforms.filter(x => x !== p) : [...prev.platforms, p];
      if (!platforms.length) platforms = [p]; // never allow empty
      const common = commonEventsFor(platforms);
      const event = common.some(o => o.v === prev.event) ? prev.event : (common[0]?.v || 'new_message');
      return { ...prev, platforms, event };
    });
  };

  const handleSubmitCreate = async () => {
    const isEvent = form.triggerType === 'event';
    const isIntent = form.triggerType === 'intent';
    if (!form.platforms.length || !form.name.trim() || (!form.editId && isIntent && !form.intent) || (!form.editId && isEvent && !form.event)) {
      setCreateErr(`Platform(s)${form.editId ? '' : `, ${isEvent ? 'event' : 'intent'},`} and name are required`);
      return;
    }
    // Demo accounts: no persistence. A config edit is blocked outright; a NEW
    // automation opens the blank editor so the full build flow is visible, and
    // only the editor's Save & Deploy is blocked.
    if (readOnly) {
      if (form.editId) { alert('Not authorized for test accounts — changes are not saved on demo accounts.'); setCreating(false); return; }
      setShowCreateModal(false);
      setCreating(false);
      navigate(`/workspace/${workspaceUuid}/automations/new`);
      return;
    }
    setCreating(true);
    setCreateErr('');
    try {
      if (form.editId) {
        // Edit configuration: name + platforms only (trigger stays as-is).
        await axios.patch(`${API_URL}/api/workflows/${form.editId}/config`, {
          name: form.name.trim(),
          platforms: form.platforms,
        });
        setShowCreateModal(false);
        fetchWorkflows();
      } else {
        let trigger;
        if (isEvent) trigger = { type: 'event', value: form.event };
        else if (form.triggerType === 'inactivity') trigger = { type: 'inactivity', value: (form.inactivityIntervals && form.inactivityIntervals.length ? form.inactivityIntervals : ['3h']).join(',') };
        else if (form.triggerType === 'chat_expiry_reminder') { const mins = Number(form.expiryH || 0) * 60 + Number(form.expiryM || 0); trigger = { type: 'chat_expiry_reminder', value: String(mins > 0 ? mins : 120) }; }
        else trigger = { type: 'intent', value: form.intent };
        const res = await axios.post(`${API_URL}/api/workflows`, {
          workspaceUuid,
          name: form.name.trim(),
          platforms: form.platforms,
          trigger,
        });
        setShowCreateModal(false);
        navigate(`/workspace/${workspaceUuid}/automations/${res.data._id}`);
      }
    } catch (err) {
      setCreateErr(err.response?.data?.error || err.message || 'Failed to save automation');
    } finally {
      setCreating(false);
    }
  };

  const wfMode = (w) => w.mode || (w.isActive ? 'active' : 'paused');

  const setWorkflowMode = async (workflowId, newMode) => {
    if (readOnly) { alert('Not authorized for test accounts — demo accounts cannot change automation status.'); return; }
    const wf = workflows.find(w => w._id === workflowId);
    const contacts = wf?.testContacts || {};
    const prev = workflows;
    setWorkflows(p => p.map(w => w._id === workflowId ? { ...w, mode: newMode, isActive: newMode !== 'paused' } : w));
    try { await axios.patch(`${API_URL}/api/workflows/${workflowId}/toggle`, { mode: newMode, testContacts: contacts }); }
    catch (err) { console.error(err); setWorkflows(prev); }
  };

  const setWorkflowTestContact = (workflowId, platform, value) => {
    setWorkflows(p => p.map(w => w._id === workflowId ? { ...w, testContacts: { ...(w.testContacts || {}), [platform]: value } } : w));
  };

  const saveWorkflowTestContacts = async (workflowId) => {
    const wf = workflows.find(w => w._id === workflowId);
    if (!wf) return;
    try { await axios.patch(`${API_URL}/api/workflows/${workflowId}/toggle`, { mode: wfMode(wf), testContacts: wf.testContacts || {} }); }
    catch (err) { console.error(err); }
  };

  const handleDelete = async (workflowId) => {
    if (!window.confirm("Are you sure you want to delete this automation?")) return;
    try {
      await axios.delete(`${API_URL}/api/workflows/${workflowId}`);
      fetchWorkflows();
    } catch (err) {
      alert("Error deleting automation.");
    }
  };

  // ---- derived (display only) ----
  const total = workflows.length;
  const activeCount = workflows.filter(w => w.isActive).length;
  const countByPlatform = (p) => workflows.filter(w => (w.platform || 'whatsapp') === p).length;

  const visibleWorkflows = workflows.filter(wf => {
    const p = wf.platform || 'whatsapp';
    if (filter !== 'all' && p !== filter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = `${wf.name || ''} ${wf.trigger?.value || ''} ${p}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  if (loading) {
    const Bar = ({ w, h = 10, r = 999, s }) => <div className="cl-skel" style={{ width: w, height: h, borderRadius: r, ...s }} />;
    const AutoSkel = () => (
      <div className="cl-skel-card" style={{ display: 'flex', flexDirection: 'column', minHeight: 232 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 8 }}>{[0, 1].map(i => <div key={i} className="cl-skel cl-skel-circle" style={{ width: 42, height: 42 }} />)}</div>
          <Bar w={78} h={22} />
        </div>
        <Bar w="55%" h={16} s={{ marginTop: 16 }} />
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}><Bar w={70} h={20} /><Bar w={92} h={20} /></div>
        <Bar w={120} h={10} s={{ marginTop: 14 }} />
        <div style={{ marginTop: 'auto', paddingTop: 16, borderTop: '1px solid #F1ECE2', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <Bar w={150} h={28} />
          <div style={{ display: 'flex', gap: 8 }}><Bar w={64} h={28} /><Bar w={64} h={28} /></div>
        </div>
      </div>
    );
    return (
      <div className="ui-page">
        <div className="ui-head">
          <div>
            <h1 className="ui-title">Automations</h1>
            <p className="ui-sub">Build intelligent workflows that trigger instantly based on intent.</p>
          </div>
        </div>
        <div className="ui-stats cl-skel-stagger" style={{ marginBottom: 22 }}>
          {[0, 1, 2].map(i => (
            <div key={i} className="cl-skel-card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px' }}>
              <div className="cl-skel cl-skel-circle" style={{ width: 38, height: 38, flex: 'none' }} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}><Bar w="50%" h={18} /><Bar w="35%" h={9} /></div>
            </div>
          ))}
        </div>
        <div className="auto-grid cl-skel-stagger">{Array.from({ length: 6 }).map((_, i) => <AutoSkel key={i} />)}</div>
      </div>
    );
  }

  const tabs = [
    { v: 'all', label: 'All', n: total },
    { v: 'whatsapp', label: 'WhatsApp', n: countByPlatform('whatsapp') },
    { v: 'instagram', label: 'Instagram', n: countByPlatform('instagram') },
    { v: 'facebook', label: 'Messenger', n: countByPlatform('facebook') },
    { v: 'website', label: 'Website', n: countByPlatform('website') },
  ];

  return (
    <div className="ui-page">
      <style>{autoCss}</style>
      {/* header */}
      <div className="ui-head">
        <div>
          <h1 className="ui-title">Automations</h1>
          <p className="ui-sub">Build intelligent workflows that trigger instantly based on intent.</p>
        </div>
        <div className="ui-head-actions">
          <button className="ui-btn" onClick={handleCreateNew}>
            <IconPlus size={16} /> New Automation
          </button>
        </div>
      </div>

      {/* stats */}
      <div className="ui-stats">
        <div className="ui-stat tone-slate">
          <span className="ui-stat-ic"><IconBolt /></span>
          <div>
            <div className="ui-stat-n">{total}</div>
            <div className="ui-stat-l">Total Workflows</div>
          </div>
        </div>
        <div className="ui-stat tone-emerald">
          <span className="ui-stat-ic"><IconPower /></span>
          <div>
            <div className="ui-stat-n">{activeCount}</div>
            <div className="ui-stat-l">Active</div>
          </div>
        </div>
        <div className="ui-stat tone-amber">
          <span className="ui-stat-ic"><IconPower /></span>
          <div>
            <div className="ui-stat-n">{total - activeCount}</div>
            <div className="ui-stat-l">Paused</div>
          </div>
        </div>
      </div>

      {/* filter tabs + search */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div className="ui-tabs" style={{ marginBottom: 0, border: 'none', flex: 1 }}>
          {tabs.map(t => (
            <button key={t.v} className={`ui-tab ${filter === t.v ? 'on' : ''}`} onClick={() => setFilter(t.v)}>
              {t.label} <span className="ui-tab-n">{t.n}</span>
            </button>
          ))}
        </div>
        <div className="ui-search">
          <IconSearch size={16} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search automations…"
          />
        </div>
      </div>

      <div style={{ borderBottom: '1px solid #E7E1D6', margin: '8px 0 22px' }} />

      {/* create modal */}
      {showCreateModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(17,17,17,0.3)', backdropFilter: 'blur(2px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowCreateModal(false); }}
        >
          <div style={{ background: '#FFFFFF', borderRadius: 20, padding: '24px 26px', width: 480, maxWidth: '100%', border: '1px solid #E7E1D6', boxShadow: '4px 4px 0 rgba(17,17,17,.2)', animation: 'uiIn .25s ease both' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: '#111111', letterSpacing: '-0.03em' }}>{form.editId ? 'Edit Configuration' : 'New Automation'}</h2>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6B6862' }}>{form.editId ? 'Update the name and platforms this automation runs on.' : 'Pick one or more platforms and a trigger before opening the editor.'}</p>
              </div>
              <button type="button" onClick={() => setShowCreateModal(false)} style={{ background: 'none', border: 'none', color: '#9C978C', cursor: 'pointer', padding: 4, display: 'flex' }}>
                <IconClose size={18} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#6B6862', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 7 }}>Platforms <span style={{ color: '#9C978C', textTransform: 'none', letterSpacing: 0 }}>· select one or more</span></label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {PLATFORMS.map(opt => {
                    const active = form.platforms.includes(opt.v);
                    const PIcon = opt.Icon;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => togglePlatform(opt.v)}
                        style={{
                          position: 'relative',
                          flex: 1,
                          padding: '12px 8px',
                          borderRadius: 14,
                          border: active ? '1.5px solid #111111' : '1px solid #E7E1D6',
                          background: active ? '#111111' : '#fff',
                          color: active ? '#F9F4EE' : '#6B6862',
                          fontWeight: 700,
                          fontSize: 12.5,
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 6,
                          transition: 'all .15s'
                        }}
                      >
                        {active && (
                          <span style={{ position: 'absolute', top: 5, right: 5, width: 15, height: 15, borderRadius: '50%', background: '#F9F4EE', color: '#111111', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                          </span>
                        )}
                        <span style={{ width: 40, height: 40, borderRadius: '50%', background: opt.bg, color: opt.color, display: 'grid', placeItems: 'center', boxShadow: 'inset 0 0 0 1px rgba(17,17,17,.05)', flex: 'none' }}>
                          <PIcon size={20} />
                        </span>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {form.platforms.length > 1 && (
                  <div style={{ fontSize: 11, color: '#9C978C', marginTop: 6 }}>Runs on {form.platforms.length} platforms. Event triggers show only events common to all selected.</div>
                )}
              </div>

              {!form.editId && (<>
              {/* Trigger type */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#6B6862', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 7 }}>Trigger Type</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {[
                    { v: 'intent', label: 'Intent', desc: 'When AI detects an intent' },
                    { v: 'event', label: 'Event', desc: 'On a message or store event' },
                    { v: 'inactivity', label: 'Customer No-Reply (inactivity)', desc: 'When a customer stops replying' },
                    { v: 'chat_expiry_reminder', label: 'Chat Expiry Reminder', desc: 'When the session window is about to expire' },
                  ].map(t => {
                    const disabled = false;
                    const active = form.triggerType === t.v;
                    return (
                      <button key={t.v} type="button" disabled={disabled}
                        onClick={() => setForm(prev => ({ ...prev, triggerType: t.v }))}
                        title={t.desc}
                        style={{
                          flex: '1 1 45%', padding: '11px 10px', borderRadius: 11, textAlign: 'left',
                          border: active ? '1.5px solid #111111' : '1px solid #E7E1D6',
                          background: active ? '#111111' : '#fff', color: active ? '#fff' : (disabled ? '#9C978C' : '#111111'),
                          fontWeight: 700, fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all .15s'
                        }}>
                        {t.label}
                        <div style={{ fontSize: 10.5, fontWeight: 600, color: active ? 'rgba(255,255,255,.7)' : '#9C978C', marginTop: 2 }}>{t.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {form.triggerType === 'intent' ? (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#6B6862', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 7 }}>Trigger Intent</label>
                  <select
                    value={form.intent}
                    onChange={(e) => setForm(prev => ({ ...prev, intent: e.target.value }))}
                    style={{ width: '100%', padding: '11px 12px', borderRadius: 11, border: '1px solid #E7E1D6', fontSize: 14, outline: 'none', background: 'white', color: '#111111', cursor: 'pointer' }}
                  >
                    <option value="">Select an intent tag…</option>
                    {availableIntents.map(t => (
                      <option key={t._id || t.name} value={t.name}>{t.name}</option>
                    ))}
                  </select>
                  {availableIntents.length === 0 && <div style={{ fontSize: 11, color: '#9C978C', marginTop: 5 }}>No intent tags yet — create some in Intent Tags first.</div>}
                </div>
              ) : form.triggerType === 'event' ? (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#6B6862', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 7 }}>Trigger Event</label>
                  <select
                    value={form.event}
                    onChange={(e) => setForm(prev => ({ ...prev, event: e.target.value }))}
                    style={{ width: '100%', padding: '11px 12px', borderRadius: 11, border: '1px solid #E7E1D6', fontSize: 14, outline: 'none', background: 'white', color: '#111111', cursor: 'pointer' }}
                  >
                    {commonEventsFor(form.platforms).map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                  </select>
                  <div style={{ fontSize: 11, color: '#9C978C', marginTop: 5 }}>
                    {form.event === 'new_message' ? 'Fires whenever a selected channel receives any inbound message.' : form.event === 'agent_query_resolved' ? 'Fires internally when the "Ask: Query Resolved?" block routes here.' : 'Fires automatically from your connected Shopify store.'}
                  </div>
                </div>
              ) : form.triggerType === 'inactivity' ? (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#6B6862', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 7 }}>No Customer Reply For · select one or more</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                    {[{ v: '1m', l: '1 min' }, { v: '3m', l: '3 min' }, { v: '5m', l: '5 min' }, { v: '3h', l: '3 hours' }, { v: '6h', l: '6 hours' }, { v: '12h', l: '12 hours' }, { v: '18h', l: '18 hours' }, { v: '23h', l: '23 hours' }].map(o => {
                      const on = (form.inactivityIntervals || []).includes(o.v);
                      return (
                        <div key={o.v} role="checkbox" aria-checked={on} tabIndex={0}
                          onClick={() => setForm(prev => { const cur = prev.inactivityIntervals || []; const next = cur.includes(o.v) ? cur.filter(x => x !== o.v) : [...cur, o.v]; return { ...prev, inactivityIntervals: next }; })}
                          style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 12px', borderRadius: 12, border: `1.5px solid ${on ? '#111111' : '#E7E1D6'}`, background: on ? '#FAF7F1' : '#fff', color: on ? '#111111' : '#3A3A37', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', userSelect: 'none', transition: 'all .12s' }}>
                          <span style={{ width: 18, height: 18, borderRadius: 6, border: `1.5px solid ${on ? '#111111' : '#9C978C'}`, background: on ? '#111111' : '#fff', display: 'grid', placeItems: 'center', flex: 'none' }}>
                            {on && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                          </span>
                          {o.l}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: 11, color: '#9C978C', marginTop: 7 }}>Sends the reminder at each selected mark since the customer's last reply.</div>
                </div>
              ) : (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#6B6862', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 7 }}>Trigger When Time Remaining</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="number" min="0" max="23" value={form.expiryH} onChange={(e) => setForm(prev => ({ ...prev, expiryH: e.target.value }))} placeholder="hours"
                      style={{ width: '50%', padding: '11px 12px', borderRadius: 11, border: '1px solid #E7E1D6', fontSize: 14, outline: 'none', boxSizing: 'border-box', color: '#111111' }} />
                    <span style={{ fontWeight: 800, color: '#6B6862', fontSize: 18 }}>:</span>
                    <input type="number" min="0" max="59" value={form.expiryM} onChange={(e) => setForm(prev => ({ ...prev, expiryM: e.target.value }))} placeholder="minutes"
                      style={{ width: '50%', padding: '11px 12px', borderRadius: 11, border: '1px solid #E7E1D6', fontSize: 14, outline: 'none', boxSizing: 'border-box', color: '#111111' }} />
                  </div>
                  <div style={{ fontSize: 11, color: '#9C978C', marginTop: 5 }}>Hours : minutes before the 24h WhatsApp window closes.</div>
                </div>
              )}
              </>)}

              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#6B6862', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 7 }}>Automation Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Refund Auto-Reply"
                  style={{ width: '100%', padding: '11px 12px', borderRadius: 11, border: '1px solid #E7E1D6', fontSize: 14, outline: 'none', boxSizing: 'border-box', color: '#111111' }}
                />
              </div>

              {createErr && (
                <div style={{ fontSize: 12.5, color: '#B23A3A', background: '#F6E4E1', border: '1px solid #B23A3A', padding: '9px 12px', borderRadius: 10, fontWeight: 600 }}>{createErr}</div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button type="button" className="ui-btn ui-btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="button" className="ui-btn" style={{ flex: 2, justifyContent: 'center' }} onClick={handleSubmitCreate} disabled={creating}>
                  {creating ? 'Saving…' : (form.editId ? 'Save Changes' : 'Create & Open Editor')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* list / empty */}
      {workflows.length === 0 ? (
        <div className="ui-empty">
          <div className="ui-empty-ic"><IconBolt size={28} /></div>
          <h2>No Automations Found</h2>
          <p>Get started by creating your first intent-driven workflow to automatically message customers.</p>
          <button className="ui-btn" style={{ margin: '0 auto' }} onClick={handleCreateNew}>
            <IconPlus size={16} /> Create Workflow
          </button>
        </div>
      ) : visibleWorkflows.length === 0 ? (
        <div className="ui-empty">
          <div className="ui-empty-ic"><IconSearch size={26} /></div>
          <h2>No matches</h2>
          <p>No automations match your current filter or search.</p>
        </div>
      ) : (
        <div className="auto-grid">
          {visibleWorkflows.map((wf, i) => {
            const wfPlatforms = (wf.platforms && wf.platforms.length) ? wf.platforms : [wf.platform || 'whatsapp'];
            const triggerLabel = wf.trigger?.type === 'event' ? 'Event'
              : wf.trigger?.type === 'inactivity' ? 'Inactivity'
              : wf.trigger?.type === 'chat_expiry_reminder' ? 'Chat Expiry'
              : 'Intent';
            const actions = wf.actions?.length || 0;
            return (
              <div key={wf._id} className="auto-card" style={{ animationDelay: `${Math.min(i * 40, 320)}ms` }}>
                <div className="auto-top">
                  <span className="auto-icons">
                    {wfPlatforms.map(p => { const pm = platformMeta(p); const Ic = pm.Icon; return (
                      <span key={p} className="auto-icon" style={{ background: pm.bg, color: pm.color }} title={pm.label}>
                        <Ic size={22} />
                      </span>
                    ); })}
                  </span>
                  {(() => { const m = wfMode(wf); const c = m === 'active' ? '#0F6E56' : m === 'testing' ? '#8A5A12' : '#6B6862'; const bg = m === 'active' ? '#E4F1E9' : m === 'testing' ? '#F5EAD5' : '#FAF7F1';
                    return (
                      <span className="auto-status" style={{ color: c, background: bg }}>
                        <span className="auto-status-dot" style={{ background: c }} />{m === 'active' ? 'Active' : m === 'testing' ? 'Testing' : 'Paused'}
                      </span>
                    ); })()}
                </div>

                <h3 className="auto-name" title={wf.name}>{wf.name}</h3>

                <div className="auto-meta">
                  {wfPlatforms.map(p => { const pm = platformMeta(p); return (
                    <span key={p} className="auto-platform" style={{ color: pm.color, background: pm.bg }}>{pm.label}</span>
                  ); })}
                  <span className="auto-trigger" title={wf.trigger?.value || ''}>
                    <IconTarget size={13} />
                    <span className="auto-trigger-label">{triggerLabel}</span>
                    <span className="auto-trigger-val">{wf.trigger?.value || '—'}</span>
                  </span>
                </div>

                <div className="auto-count">{actions} action{actions === 1 ? '' : 's'} configured</div>

                <div className="auto-foot">
                  <div style={{ display: 'inline-flex', background: '#FAF7F1', borderRadius: 999, padding: 2, gap: 2, border: '1px solid #E7E1D6' }}>
                    {[{ v: 'paused', l: 'Paused', c: '#6B6862' }, { v: 'testing', l: 'Testing', c: '#8A5A12' }, { v: 'active', l: 'Active', c: '#0F6E56' }].map(m => {
                      const on = wfMode(wf) === m.v;
                      return (
                        <button key={m.v} onClick={() => setWorkflowMode(wf._id, m.v)} disabled={readOnly}
                          title={readOnly ? 'Not available on demo accounts' : undefined}
                          style={{ border: 'none', cursor: readOnly ? 'not-allowed' : 'pointer', padding: '5px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, background: on ? '#111111' : 'transparent', color: on ? '#F9F4EE' : '#9C978C', opacity: readOnly ? 0.55 : 1, boxShadow: 'none' }}>{m.l}</button>
                      );
                    })}
                  </div>
                  <div className="auto-foot-btns">
                    <button className="ui-btn ui-btn-ghost ui-btn-sm" onClick={() => handleEditConfig(wf)} title="Edit name & platforms">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg> Config
                    </button>
                    <button className="ui-btn ui-btn-ghost ui-btn-sm" onClick={() => navigate(`/workspace/${workspaceUuid}/automations/${wf._id}`)}>
                      <IconEdit size={14} /> Edit flow
                    </button>
                    {!readOnly && (
                    <button className="ui-btn ui-btn-danger ui-btn-sm" onClick={() => handleDelete(wf._id)} aria-label="Delete automation">
                      <IconTrash size={14} />
                    </button>
                    )}
                  </div>
                </div>
                {wfMode(wf) === 'testing' && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10, paddingTop: 10, borderTop: '1px dashed #8A5A12' }}>
                    {wfPlatforms.map(p => (
                      <input key={p} value={(wf.testContacts || {})[p] || ''}
                        onChange={(e) => setWorkflowTestContact(wf._id, p, e.target.value)}
                        onBlur={() => saveWorkflowTestContacts(wf._id)}
                        placeholder={p === 'whatsapp' ? `${p}: 923001234567` : p === 'website' ? `${p}: visitor id` : `${p}: user id`}
                        style={{ padding: '6px 9px', fontSize: 11.5, borderRadius: 7, border: '1px solid #8A5A12', outline: 'none', background: '#F5EAD5', color: '#111111', minWidth: 130, flex: 1 }} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const autoCss = `
.auto-grid{ display:grid; grid-template-columns:repeat(auto-fill, minmax(330px, 1fr)); gap:16px; }
.auto-card{ background:#FFFFFF; border:1px solid #E7E1D6; border-radius:14px; padding:20px 20px 18px; display:flex; flex-direction:column; box-shadow:none; transition:box-shadow .15s, transform .15s, border-color .15s; animation:uiIn .4s ease both; }
.auto-card:hover{ box-shadow:4px 4px 0 rgba(17,17,17,.2); transform:translateY(-2px); border-color:#E7E1D6; }

.auto-top{ display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; }
.auto-icons{ display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.auto-icon{ width:42px; height:42px; border-radius:50%; display:grid; place-items:center; flex:none; box-shadow:inset 0 0 0 1px rgba(17,17,17,.05); }
.auto-icon svg{ width:21px; height:21px; }
.auto-status{ display:inline-flex; align-items:center; gap:6px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; padding:4px 11px; border-radius:999px; }
.auto-status[data-on="true"]{ color:#0F6E56; background:#E4F1E9; }
.auto-status[data-on="false"]{ color:#6B6862; background:#FAF7F1; }
.auto-status-dot{ width:6px; height:6px; border-radius:50%; background:currentColor; }

.auto-name{ margin:0 0 13px; font-size:17px; font-weight:700; color:#111111; letter-spacing:-0.03em; line-height:1.3; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

.auto-meta{ display:flex; flex-wrap:wrap; gap:8px; align-items:center; margin-bottom:14px; }
.auto-platform{ font-size:11.5px; font-weight:700; padding:3px 10px; border-radius:999px; white-space:nowrap; }
.auto-trigger{ display:inline-flex; align-items:center; gap:6px; font-size:12px; color:#6B6862; background:#FAF7F1; border:1px solid #E7E1D6; padding:3px 9px; border-radius:8px; min-width:0; max-width:100%; }
.auto-trigger-label{ font-weight:700; color:#3A3A37; }
.auto-trigger-val{ font-weight:500; font-family:'IBM Plex Mono', monospace; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

.auto-count{ font-size:12.5px; color:#9C978C; font-weight:600; font-family:'IBM Plex Mono', monospace; padding-bottom:16px; margin-bottom:14px; border-bottom:1px solid #E7E1D6; }

.auto-foot{ display:flex; align-items:center; justify-content:space-between; gap:10px; margin-top:auto; flex-wrap:wrap; row-gap:12px; }
.auto-foot-btns{ display:flex; gap:8px; flex-wrap:wrap; justify-content:flex-end; }
.auto-foot-btns .ui-btn-sm{ padding:6px 10px; }

@media (max-width:560px){ .auto-grid{ grid-template-columns:1fr; } .auto-foot{ flex-wrap:wrap; } }
`;

export default AutomationsList;
